import { useRef } from "preact/hooks";
import { cx } from "../cx";

export interface TabItem {
  id: string;
  label: string;
  /** Small marker after the label, e.g. "*" for unsaved changes. */
  mark?: string;
}

export interface TabsProps {
  tabs: readonly TabItem[];
  active: string;
  onChange: (id: string) => void;
  /** Prefix for element ids: tab `x` is `${idPrefix}-tab-x`, its panel should be `${idPrefix}-panel-x`. */
  idPrefix: string;
  ariaLabel: string;
  class?: string;
}

export function tabId(prefix: string, id: string): string {
  return `${prefix}-tab-${id}`;
}

export function panelId(prefix: string, id: string): string {
  return `${prefix}-panel-${id}`;
}

/** Tab strip with automatic activation: Left/Right/Home/End move and select. */
export function Tabs({ tabs, active, onChange, idPrefix, ariaLabel, class: cls }: TabsProps) {
  const list = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.id === active);
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    const tab = tabs[next];
    if (!tab) return;
    e.preventDefault();
    onChange(tab.id);
    requestAnimationFrame(() => list.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
  };

  return (
    <div ref={list} class={cx("tabs", cls)} role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {tabs.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={tabId(idPrefix, t.id)}
            aria-selected={selected}
            aria-controls={panelId(idPrefix, t.id)}
            tabIndex={selected ? 0 : -1}
            class={cx("tabs__tab", selected && "is-active")}
            onClick={() => onChange(t.id)}
            data-nav={selected ? "" : undefined}
          >
            {t.label}
            {t.mark && <span class="tabs__mark">{t.mark}</span>}
          </button>
        );
      })}
    </div>
  );
}
